import type { ApiError } from '@/api/apiError'
import type { Theme } from '@/app/providers/theme-provider'
import { isRootProject } from '@/components/organization/organizationUtils'
import type { Permissions } from '@/rbac/grants'
import type { TenantQuota } from '@/types/organization-owner-api'
import { ROLE_ADMIN } from '@/utils/tenant'
import type { AbsoluteTimeRange, DurationInputArg } from './timerange'

export type ApiResponse<T, K extends string = 'result'> = Record<K, T>

export type ShowTabsData = {
	device_management: { show: boolean }
	dashboards: { show: boolean }
	remote_commands: { show: boolean }
	dbc_parsers: { show: boolean }
	exporters: { show: boolean }
	sessions: { show: boolean }
	audit_logs: { show: boolean }
	ota_campaigns: { show: boolean }
	jobs: { show: boolean }
}

export interface BaseUser {
	name: string
	email: string
}

export interface TenantRole {
	tenant_id: string
	name: string
}

// Unified Organization type
export interface Organization {
	id: string
	name: string
	domain?: string
	created_at: string
	created_by: string
	owners: string[]
	frontend_settings?: OrgFrontendSettings
	broker_host?: string | null
	broker_port?: number | null
	// Platform-only: effective subscription state used for displaying warnings.
	// This differs from subscription.state which is the Chargebee state.
	effective_subscription_state?: string
	subscription?: {
		plan_name?: string
		plan_description?: string
		// Chargebee subscription state (e.g. 'active', 'cancelled')
		state?: string
		period?: string
		expires_at?: string
		updated_at?: string
		remarks?: string | null
		quota?: {
			max_tenants?: number
			per_tenant_quota?: Partial<TenantQuota> & {
				[key: string]: number | undefined
			}
			[key: string]: unknown
		}
	}
}

// Console user type - returned from console.bytebeam.io /v1/me endpoint
export interface ConsoleUser {
	name: string
	email: string
	organizations: Organization[]
}

type ClientType = 'oauth' | 'api_key'

export interface Tenant {
	id: string
	name: string
	org_id: string
	created_at: string
	created_by: string
	deactivated_at: string | null
	deactivated_by: string | null
	organization: Organization
	/** True for the organization-level (root) project. */
	is_org?: boolean
}

export interface PlatformUserApiResponse {
	tenants: Tenant[]
	organizations: Organization[]
	user: {
		client_type: ClientType
		email: string
		name: string
		role: (Role & { tenant_id: string }) | null
		settings?: UserSettings
	}
	server_settings: { infra_service_url: string }
	tenant_settings?: TenantSettings
}

// Platform user type - transformed from /v3/me API response
export interface PlatformUser {
	id?: string
	name: string
	email: string
	tenants: string[]
	tenant_list?: Tenant[]
	tenant_id?: string
	role: Role | null
	'tenant-roles'?: TenantRole[]
	settings?: UserSettings
	'tenant-settings'?: TenantSettings
	organizations?: Organization[]
}

// Type guards for user types
export function isConsoleUser(user: ConsoleUser | PlatformUser | null): user is ConsoleUser {
	if (!user) {
		return false
	}
	return 'organizations' in user && Array.isArray((user as ConsoleUser).organizations) && !('tenants' in user)
}

export function isPlatformUser(user: ConsoleUser | PlatformUser | null): user is PlatformUser {
	if (!user) {
		return false
	}
	return 'tenants' in user && Array.isArray((user as PlatformUser).tenants) && 'role' in user
}

export function isUserOwner(user: ConsoleUser | PlatformUser | null | undefined): boolean {
	if (!user || !user.email) {
		return false
	}
	const email = user.email
	if (user.organizations?.some(org => org.owners?.includes(email))) {
		return true
	}
	if (isPlatformUser(user) && user.tenant_list) {
		return user.tenant_list.some(t => t.organization?.owners?.includes(email))
	}
	return false
}

export function isUserAdmin(user: ConsoleUser | PlatformUser | null | undefined): boolean {
	if (!user) {
		return false
	}
	if (isPlatformUser(user)) {
		return user.role?.name === ROLE_ADMIN
	}
	return false
}

export type UserInfo = BaseUser & { role_name: string; tenantName?: string; tenantId?: string }
export type UserListResponse = { users: UserInfo[] }
export type UserInfoWithId = UserInfo & { id: string; isOrgOwner?: boolean }

/** One page's saved display preferences, keyed by pref name (e.g. `groupBy`, `selectedProperties`). */
export type DisplayPreferencePage = Record<string, unknown>
/** One scope's (tenant or org) display preferences, keyed by page key (e.g. `remoteCommands`). */
export type DisplayPreferenceScope = Record<string, DisplayPreferencePage>

export interface UserSettings {
	theme?: Theme
	current_release_id?: string
	changelog_id?: string
	selectedDevice?: string
	/** Personal pinned default organization. */
	default_organization?: string
	/** Personal pinned default project per organization (orgId -> tenantId). */
	default_projects?: Record<string, string>
	/** Per-tenant UI display prefs (columns, grouping, tab order), keyed by `${orgId}:${tenantId}` then by page key. */
	displayPreferences?: Record<string, DisplayPreferenceScope>
}

export type CustomTimeRanges = Record<
	string,
	| AbsoluteTimeRange
	| {
			to: { date: string }
			from: { date: string }
	  }
	| {
			to: {
				units: DurationInputArg
				duration: number
			}
			from: {
				units: DurationInputArg
				duration: number
			}
	  }
>

// Organization-level branding
export interface Branding {
	brand_mark: string
}

// Project-level frontend settings
export interface FrontendSettings {
	common_settings: {
		pin_metadata: string[]
	}
	serial_key: string | null
	show_tabs?: ShowTabsData | null
	dashboard_settings: {
		custom_time_ranges: CustomTimeRanges
	}
	default_action_timeout?: number | Date
}

// Organization-level frontend settings (extends project settings with branding)
export interface OrgFrontendSettings extends FrontendSettings {
	branding?: Branding
}

/** True when the user belongs to the org as an owner or through any tenant membership. */
function userBelongsToOrganization(user: ConsoleUser | PlatformUser | null, orgId: string): boolean {
	if (!user) {
		return false
	}
	if (user.organizations?.some(org => org.id === orgId)) {
		return true
	}
	return isPlatformUser(user) ? (user.tenant_list?.some(tenant => tenant.org_id === orgId) ?? false) : false
}

/**
 * Merges a fresh `displayPreferences` tree with the in-memory one, instead of replacing it, so
 * a `/me` refetch racing an in-flight settings write doesn't drop the edit. Merges three levels
 * deep (scope -> page -> individual pref key) so a still-in-flight edit to one pref key survives
 * even when its page bag already exists in the fetched data (the common case, since a page
 * usually holds more than one pref key). Server value wins on exact pref-key collisions.
 */
export function mergeDisplayPreferences(
	previous: UserSettings['displayPreferences'],
	incoming: UserSettings['displayPreferences'],
): UserSettings['displayPreferences'] {
	if (!previous) {
		return incoming
	}
	if (!incoming) {
		return previous
	}
	const merged: Record<string, DisplayPreferenceScope> = { ...previous }
	for (const [scopeKey, incomingScope] of Object.entries(incoming)) {
		const previousScope = merged[scopeKey] ?? {}
		const nextScope: DisplayPreferenceScope = { ...previousScope }
		for (const [pageKey, incomingPage] of Object.entries(incomingScope)) {
			nextScope[pageKey] = { ...previousScope[pageKey], ...incomingPage }
		}
		merged[scopeKey] = nextScope
	}
	return merged
}

/** Pinned default org from the user's personal settings; ignored when the user no longer belongs to it. */
export function getDefaultOrganizationId(user: ConsoleUser | PlatformUser | null): string | null {
	if (!user || !isPlatformUser(user)) {
		return null
	}
	const pinned = user.settings?.default_organization
	return pinned && userBelongsToOrganization(user, pinned) ? pinned : null
}

/** Personal pinned project for an org from user settings; null when unset. */
export function getUserPinnedProjectId(user: PlatformUser | null, orgId: string): string | null {
	const pinned = user?.settings?.default_projects?.[orgId]
	return typeof pinned === 'string' && pinned.length > 0 ? pinned : null
}

export interface ResolvedDefaultTenant {
	orgId: string
	tenantId: string
}

export function getAccessibleTenants(user: PlatformUser | null): Tenant[] {
	if (!user?.tenant_list?.length) {
		return []
	}
	const tenants = user.tenant_list
	return tenants.filter(t => !isRootProject(t.id, tenants) && t.deactivated_at == null)
}

export function hasOnlyNonUsableTenants(user: PlatformUser | null): boolean {
	if (!user?.tenant_list?.length) {
		return false
	}
	return getAccessibleTenants(user).length === 0
}

/** Personal org pin, then personal project pins, then first non-is_org active tenant. */
export function resolveDefaultTenant(user: PlatformUser | null): ResolvedDefaultTenant | null {
	const accessible = getAccessibleTenants(user)
	if (!accessible.length) {
		return null
	}

	const pinnedOrgId = user?.settings?.default_organization
	if (pinnedOrgId) {
		const tenantId = resolveDefaultTenantForOrg(user, pinnedOrgId)
		if (tenantId) {
			return { orgId: pinnedOrgId, tenantId }
		}
	}

	for (const tenant of accessible) {
		if (getUserPinnedProjectId(user, tenant.org_id) === tenant.id) {
			return { orgId: tenant.org_id, tenantId: tenant.id }
		}
	}

	const first = accessible[0]
	return { orgId: first.org_id, tenantId: first.id }
}

export function resolveDefaultTenantForOrg(user: PlatformUser | null, orgId: string): string | null {
	const accessible = getAccessibleTenants(user).filter(t => t.org_id === orgId)
	if (!accessible.length) {
		return null
	}
	const pinnedProjectId = getUserPinnedProjectId(user, orgId)
	if (pinnedProjectId && accessible.some(tenant => tenant.id === pinnedProjectId)) {
		return pinnedProjectId
	}
	return accessible[0].id
}

export function resolveNoProjectAccessOrgId(user: PlatformUser | null): string | null {
	if (!user?.tenant_list?.length) {
		return null
	}
	const accessible = getAccessibleTenants(user)
	if (accessible.length) {
		return accessible[0].org_id
	}
	return user.tenant_list[0]?.org_id ?? null
}

export interface TenantSettings {
	name?: string
	frontend_settings?: FrontendSettings
	redirect_firmware_downloads_to_cdn?: boolean
}

export type ApiKeyStatus = 'active' | 'expired' | 'disabled'

export interface ApiKey {
	id: number
	name: string
	repr: string
	role_name: string
	createdAt?: string
	expiresAt?: string | null
	status?: ApiKeyStatus
	rate_limit?: number | bigint | null
}

export interface CreateApiKeyRequest {
	role_name: string
	name: string
	expiry_period?: number | null
	rate_limit?: number | bigint | null
}

export interface CreateApiKeyResponse {
	status: string
	id: number
	key: string
}

export interface UpdateApiKeyRequest {
	role_name?: string
	name?: string
	active?: boolean
}

export interface Role {
	name: string
	permissions?: Permissions
	tenantName?: string
}

export interface AuthState {
	user: ConsoleUser | PlatformUser | null
	userType: 'console' | 'platform' | null
	currentTenant: string
	recentTenants: string[]
	isLoading: boolean
	isAuthenticated: boolean
	error: string | null
	lastUsedProvider: string | null // Added this to store the last used OAuth provider
	currentOrganizationId: string | null // Current organization context for billing/quota
}

export interface LoginCredentials {
	provider: 'google' | 'microsoft' | 'github'
	redirectUri?: string // Optional redirect URI, if not provided, it will be auto-detected based on the domain
}

export interface AuthActions {
	// Authentication actions
	login: (credentials: LoginCredentials) => Promise<void>
	logout: () => Promise<void>
	refreshUser: () => Promise<{ user: ConsoleUser | PlatformUser }>

	// User management
	setUser: (user: ConsoleUser | PlatformUser | null) => void
	/** Merge `patch` into the platform user's personal settings and persist via PATCH /v3/me. */
	updateUserSettings: (patch: Partial<UserSettings>) => Promise<void>
	/**
	 * Like `updateUserSettings`, but `updater` gets the full settings object and returns the
	 * full replacement. Calls queue behind any in-flight write, so `updater` always sees the
	 * latest settings instead of a stale snapshot.
	 */
	updateUserSettingsWith: (updater: (current: UserSettings) => UserSettings) => Promise<void>
	/** Update the platform user's own display name and persist via PATCH /v3/me. */
	updateUserName: (name: string) => Promise<void>
	/** Update the owners list for a specific organization in auth user state. */
	updateOrgOwners: (orgId: string, owners: string[]) => void
	setCurrentTenant: (tenant: string) => void
	/** Clear `currentTenant` only when it does not belong to `orgId` (stale after org switch). */
	clearCurrentTenantUnlessBelongsToOrg: (orgId: string) => void
	/** Clear tenant/org scope tied to a deleted org so the next `/me` refresh is not scoped to it. */
	clearScopeForDeletedOrg: (orgId: string) => void
	setCurrentOrganizationId: (organizationId: string | null) => void

	// Recent tenants management
	addRecentTenant: (tenantId: string) => void
	// Tenant switching
	switchTenant: (tenantId: string, navigate?: (options: { to: string; replace?: boolean }) => void) => void

	// State management
	setLoading: (loading: boolean) => void
	setError: (error: string | null) => void
	clearError: () => void
	clearAuthState: () => void // Clear auth state on 401 (for interceptors)

	// Utility actions
	hasPermission: (permission: keyof Permissions) => boolean
	hasRole: (roleName: string) => boolean
	canAccessTenant: (tenantId: string) => boolean
	getTenantRole: (tenantId: string) => string | null
	setLastUsedProvider: (provider: string) => void
}

export type AuthStore = AuthState & AuthActions

// ApiError lives in the api layer (a leaf, @/api/apiError) so the transport can
// reference it without importing the stores. Re-exported here for back-compat.
export type { ApiError }

/** Detect 401 from ApiError (status) */
export function is401Error(error: unknown): boolean {
	if (error && typeof error === 'object' && 'status' in error && (error as ApiError).status === 401) {
		return true
	}
	const msg = (error as Error)?.message ?? ''
	return msg.includes('401') || /unauthorized/i.test(msg)
}

/**
 * If the error is 401 and we're not on /login, clear auth and redirect to login.
 * Call when auth-related requests fail (e.g. getCurrentUser). Returns true if redirect was performed.
 */
export function redirectToLoginIf401(error: unknown, clearAuthState: () => void): boolean {
	if (!is401Error(error) || typeof window === 'undefined' || window.location.pathname === '/login') {
		return false
	}
	clearAuthState()
	window.location.replace('/login')
	return true
}

export interface AuthResponse {
	user: ConsoleUser | PlatformUser
	token?: string
}

export interface OAuthConfig {
	google: {
		clientId: string
		redirectUri: string
	}
	microsoft: {
		clientId: string
		redirectUri: string
	}
	github: {
		clientId: string
		redirectUri: string
	}
}
